import { Injectable, inject, signal, computed, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, timer, switchMap, takeUntil, Subject, catchError, EMPTY, tap } from 'rxjs';
import { AuthService } from '@features/auth/services/auth.service';

@Injectable({
  providedIn: 'root'
})
export class TokenManagerService implements OnDestroy {
  private authService = inject(AuthService);
  private router = inject(Router);
  
  // Signal for tracking refresh attempts
  private refreshAttempts = signal<number>(0);
  private lastRefreshTime = signal<Date | null>(null);
  private isRefreshing = signal<boolean>(false);
  
  // Subject for cleanup
  private destroy$ = new Subject<void>();
  
  // Configuration
  private readonly MAX_REFRESH_ATTEMPTS = 3;
  private readonly REFRESH_INTERVAL_MINUTES = 45; // Refresh every 45 minutes
  private readonly TOKEN_BUFFER_MINUTES = 5; // Refresh if token expires within 5 minutes
  private readonly SESSION_EXTENSION_HOURS = 1; // Aim for 1-hour sessions
  
  // Computed signals for monitoring
  refreshStatus = computed(() => ({
    isRefreshing: this.isRefreshing(),
    lastRefresh: this.lastRefreshTime(),
    attempts: this.refreshAttempts(),
    isHealthy: this.refreshAttempts() < this.MAX_REFRESH_ATTEMPTS
  }));

  constructor() {
    this.initializeTokenManager();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize the proactive token refresh system
   */
  private initializeTokenManager(): void {
    // Start the automatic refresh timer when authenticated
    // Use effect to watch the computed isAuthenticated signal
    setInterval(() => {
      const isAuthenticated = this.authService.isAuthenticated();
      if (isAuthenticated && this.authService.getToken()) {
        this.startAutoRefresh();
      } else {
        this.stopAutoRefresh();
      }
    }, 5 * 60 * 1000); // Check every 5 minutes
  }

  /**
   * Start automatic token refresh
   */
  private startAutoRefresh(): void {
    // Clear any existing timer
    this.stopAutoRefresh();

    const refreshIntervalMs = this.REFRESH_INTERVAL_MINUTES * 60 * 1000;

    timer(0, refreshIntervalMs).pipe(
      takeUntil(this.destroy$),
      switchMap(() => this.checkAndRefreshToken()),
      catchError((error) => {
        console.error('âŒ Auto-refresh failed:', error);
        return EMPTY;
      })
    ).subscribe({
      next: (refreshed) => {
        if (refreshed) {
          this.refreshAttempts.set(0);
          this.lastRefreshTime.set(new Date());
        }
      },
      error: (error) => {
        console.error('âŒ Auto-refresh error:', error);
        this.handleRefreshFailure(error);
      }
    });
  }

  /**
   * Stop automatic token refresh
   */
  private stopAutoRefresh(): void {
    this.destroy$.next();
  }

  /**
   * Check if token needs refresh and refresh if necessary
   */
  private checkAndRefreshToken(): Observable<boolean> {
    const currentToken = this.authService.getToken();
    
    if (!currentToken) {
      return new Observable<boolean>(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    if (!this.shouldRefreshToken(currentToken)) {
      return new Observable<boolean>(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    return this.performTokenRefresh();
  }

  /**
   * Determine if token should be refreshed
   */
  private shouldRefreshToken(token: string): boolean {
    try {
      const tokenParts = token.split('.');
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const exp = payload.exp;

      if (!exp) return true;

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = exp - currentTime;
      const bufferSeconds = this.TOKEN_BUFFER_MINUTES * 60;

      // Refresh if token expires within the buffer time
      const shouldRefresh = timeUntilExpiry <= bufferSeconds;

      if (shouldRefresh) {
        const minutesUntilExpiry = Math.floor(timeUntilExpiry / 60);
      }

      return shouldRefresh;
    } catch (error) {
      console.error('âŒ Error checking token expiration:', error);
      return true;
    }
  }

  /**
   * Perform the actual token refresh
   */
  private performTokenRefresh(): Observable<boolean> {
    if (this.isRefreshing()) {
      return new Observable<boolean>(observer => {
        observer.next(false);
        observer.complete();
      });
    }

    this.isRefreshing.set(true);
    const currentAttempts = this.refreshAttempts();

    if (currentAttempts >= this.MAX_REFRESH_ATTEMPTS) {
      console.error('âŒ Max refresh attempts reached, logging out');
      this.handleMaxAttemptsReached();
      return new Observable<boolean>(observer => {
        observer.next(false);
        observer.complete();
      });
    }
         return this.authService.refreshAccessToken().pipe(
       tap(() => {
         this.isRefreshing.set(false);
         this.refreshAttempts.set(0);
         this.lastRefreshTime.set(new Date());
       }),
       switchMap(() => new Observable<boolean>(observer => {
         observer.next(true);
         observer.complete();
       })),
       catchError((error) => {
         this.isRefreshing.set(false);
         this.refreshAttempts.set(currentAttempts + 1);
         console.error('âŒ Token refresh failed:', error);
         
         // If this was the last attempt, handle failure
         if (currentAttempts + 1 >= this.MAX_REFRESH_ATTEMPTS) {
           this.handleMaxAttemptsReached();
         }
         
         return new Observable<boolean>(observer => {
           observer.next(false);
           observer.complete();
         });
       })
     );
  }

  /**
   * Handle refresh failure scenarios
   */
  private handleRefreshFailure(error: unknown): void {
    const attempts = this.refreshAttempts();
    
    console.error(`âŒ Token refresh failed (attempt ${attempts}/${this.MAX_REFRESH_ATTEMPTS}):`, error);
    
    if (attempts >= this.MAX_REFRESH_ATTEMPTS) {
      this.handleMaxAttemptsReached();
    }
  }

  /**
   * Handle max refresh attempts reached
   */
  private handleMaxAttemptsReached(): void {
    console.error('âŒ Maximum token refresh attempts reached. Logging out user.');
    this.stopAutoRefresh();
    this.authService.logout();
    this.router.navigate(['/auth/login'], { 
      queryParams: { reason: 'session_expired' } 
    });
  }

  /**
   * Manual token refresh (for user-triggered refresh)
   */
  manualRefresh(): Observable<boolean> {
    return this.performTokenRefresh();
  }

  /**
   * Force refresh regardless of expiration time
   */
  forceRefresh(): Observable<boolean> {
    this.isRefreshing.set(false); // Reset flag
    return this.performTokenRefresh();
  }

  /**
   * Get token refresh status for debugging
   */
  getRefreshStatus() {
    const token = this.authService.getToken();
    let tokenInfo = null;

    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        const exp = payload.exp;
        const currentTime = Math.floor(Date.now() / 1000);
        const timeUntilExpiry = exp - currentTime;
        
        tokenInfo = {
          expiresAt: new Date(exp * 1000).toISOString(),
          timeUntilExpiry: timeUntilExpiry,
          minutesUntilExpiry: Math.floor(timeUntilExpiry / 60),
          shouldRefresh: this.shouldRefreshToken(token)
        };
      } catch (error) {
        tokenInfo = { error: 'Failed to parse token' };
      }
    }

    return {
      refreshStatus: this.refreshStatus(),
      tokenInfo,
      configuration: {
        refreshIntervalMinutes: this.REFRESH_INTERVAL_MINUTES,
        tokenBufferMinutes: this.TOKEN_BUFFER_MINUTES,
        maxRefreshAttempts: this.MAX_REFRESH_ATTEMPTS,
        sessionExtensionHours: this.SESSION_EXTENSION_HOURS
      }
    };
  }

  /**
   * Reset refresh attempts (useful for testing)
   */
  resetRefreshAttempts(): void {
    this.refreshAttempts.set(0);
  }

  /**
   * Extend user session by forcing a refresh
   */
  extendSession(): Observable<boolean> {
    return this.forceRefresh();
  }
}
 